import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { stakeholderCohortApi, setDemoViewOnly } from '../services/api';

// Board / Senior-Management demo cohort login (2026-06-16).
//
// Net-new capability on the existing `stakeholder` role: a SHARED, owner-side,
// view-only login that can SWITCH among a fixed cohort of owners. This context
// is the SPA side of that:
//   - detects the designated demo login (GET /auth/stakeholder/cohort),
//   - holds the cohort + currently-viewed owner,
//   - drives the switch (POST /auth/stakeholder/view → hard reload),
//   - arms the shared "visible-but-dead" write-suppression layer in api.js.
//
// A normal single-owner stakeholder gets { is_demo_login: false } and is left
// completely unaffected (no picker, no banner, today's behaviour).
const DemoCohortContext = createContext({
  loading: false,
  isDemoLogin: false,
  owners: [],
  viewingOwnerContactId: null,
  viewingOwner: null,
  needsSelection: false,
  isViewOnlyDemo: false,
  pickerOpen: false,
  switching: false,
  error: '',
  openPicker: () => {},
  closePicker: () => {},
  switchOwner: () => {}
});

export function DemoCohortProvider({ children }) {
  const { authenticated, user, demoCohort } = useAuth();
  const [state, setState] = useState({
    loading: false,
    isDemoLogin: false,
    owners: [],
    viewingOwnerContactId: null
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');

  // Only stakeholders (pre-switch) or an already-switched cohort session
  // (post-switch: role becomes owner_contact + demo_cohort claim present) can
  // possibly be the demo login. Every other user skips the /cohort round-trip.
  const role = user?.role;
  const demoCohortKey = demoCohort ? (demoCohort.viewed_owner_contact_id || 'switched') : null;
  const couldBeDemo = !!authenticated && (role === 'stakeholder' || !!demoCohort);

  useEffect(() => {
    let cancelled = false;
    if (!couldBeDemo) {
      setState({ loading: false, isDemoLogin: false, owners: [], viewingOwnerContactId: null });
      setDemoViewOnly(false);
      return undefined;
    }
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      try {
        const { status, data } = await stakeholderCohortApi.getCohort();
        if (cancelled) return;
        if (status === 200 && data && data.is_demo_login) {
          setState({
            loading: false,
            isDemoLogin: true,
            owners: Array.isArray(data.owners) ? data.owners : [],
            viewingOwnerContactId: data.viewing_owner_contact_id || null
          });
          setDemoViewOnly(true);
        } else {
          setState({ loading: false, isDemoLogin: false, owners: [], viewingOwnerContactId: null });
          setDemoViewOnly(false);
        }
      } catch {
        if (cancelled) return;
        setState({ loading: false, isDemoLogin: false, owners: [], viewingOwnerContactId: null });
        setDemoViewOnly(false);
      }
    })();
    return () => { cancelled = true; };
    // demoCohortKey changes when the viewed owner changes (post-reload).
  }, [couldBeDemo, role, demoCohortKey]);

  const openPicker = useCallback(() => { setError(''); setPickerOpen(true); }, []);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  const switchOwner = useCallback(async (ownerContactId) => {
    if (!ownerContactId) return;
    setSwitching(true);
    setError('');
    try {
      const { status, data } = await stakeholderCohortApi.switchOwner(ownerContactId);
      if (status === 200) {
        // The session cookie now points at the viewed owner. A hash-only
        // change wouldn't re-run /auth/me, so force a HARD reload (mirrors the
        // act-as flow) and land on Home as the newly-viewed owner.
        if (typeof window !== 'undefined') {
          window.location.hash = '#/';
          window.location.reload();
        }
        return;
      }
      setError((data && (data.message || data.error)) || `Couldn't switch owner (HTTP ${status}).`);
      setSwitching(false);
    } catch {
      setError("Something went wrong — we're looking into it.");
      setSwitching(false);
    }
  }, []);

  const viewingOwner =
    state.owners.find((o) => o.owner_contact_id === state.viewingOwnerContactId) || null;
  // Demo login that has not yet picked an owner → must select before the portal.
  const needsSelection = state.isDemoLogin && !state.viewingOwnerContactId;

  const value = {
    loading: state.loading,
    isDemoLogin: state.isDemoLogin,
    owners: state.owners,
    viewingOwnerContactId: state.viewingOwnerContactId,
    viewingOwner,
    needsSelection,
    isViewOnlyDemo: state.isDemoLogin,
    pickerOpen,
    switching,
    error,
    openPicker,
    closePicker,
    switchOwner
  };

  return <DemoCohortContext.Provider value={value}>{children}</DemoCohortContext.Provider>;
}

export function useDemoCohort() {
  return useContext(DemoCohortContext);
}
